/* eslint-disable formatjs/no-literal-string-in-jsx -- Please check and fix */
import cx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import { useReducer, forwardRef } from 'react';
import type { TextFieldProps as AriaTextFieldProps, ValidationResult } from 'react-aria-components';
import { TextField as AriaTextField } from 'react-aria-components';
import { useIntl } from 'react-intl';
import { Description, Error, Input, Label } from '../form-elements';
import { EyeIcon } from './icons/eye';
import { EyeHiddenIcon } from './icons/eye-hidden';
import styles from './text-field.module.css';

export interface TextFieldComponentProps extends AriaTextFieldProps {
  label: string | ReactNode;
  description?: string | ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string) | ReactNode;
  placeholder: string;
  multiple?: boolean;
  isOptional?: boolean;
  isOptionalText?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldComponentProps>(function TextField(
  {
    label,
    description,
    errorMessage,
    placeholder,
    className,
    type,
    multiple,
    isOptional,
    isOptionalText,
    ...props
  },
  ref
): ReactElement {
  const { formatMessage } = useIntl();
  const [isPasswordVisible, togglePasswordVisibility] = useReducer(state => !state, false);

  return (
    <AriaTextField
      className={cx(className, styles.container)}
      type={!isPasswordVisible ? type : 'text'}
      {...props}
    >
      {Boolean(label) && (
        <Label>
          {label}{' '}
          {Boolean(isOptional) &&
            (isOptionalText || formatMessage({ id: 'form-elements-optional' }))}
        </Label>
      )}
      <span className={styles['input-container']}>
        <Input multiple={multiple} placeholder={placeholder} ref={ref} />

        {type === 'password' && (
          <button
            aria-label="Toggle password visibility"
            className={styles.icon}
            onClick={togglePasswordVisibility}
            type="button"
          >
            {isPasswordVisible ? (
              <EyeHiddenIcon aria-label="Password visible" />
            ) : (
              <EyeIcon aria-label="Password hidden" />
            )}
          </button>
        )}
      </span>
      <Error>{errorMessage}</Error>
      {Boolean(description) && <Description>{description}</Description>}
    </AriaTextField>
  );
});
